import { matchPath } from "react-router-dom";
import { ModalTypes } from "../Interfaces/ContextInterfaces/GeneralContext";

type Section = ModalTypes['type']

export type AppArea = 'school' | 'schoolAdmin' | 'superAdmin'

export interface AppLocation {
	area: AppArea
	// The school the sections belong to. A super admin outside of a school has none.
	schoolSK?: string
	// Missing when the path is inside the app but does not match any section.
	section?: Section
	itemId?: string
}

const SCHOOL_BASE_PATTERNS: Record<AppArea, string> = {
	school: '/school/:schoolSK',
	schoolAdmin: '/admin/school/:schoolSK',
	superAdmin: '/superadmin/schools/:schoolSK',
}

const SUPER_ADMIN_HOME_PATH = '/superadmin'

// Checked before the school sections, so 'schools/new' is not read as a school id.
const SUPER_ADMIN_SECTION_PATHS: [Section, string][] = [
	['SchoolList', SUPER_ADMIN_HOME_PATH],
	['SchoolCreater', '/superadmin/schools/new'],
	['SchoolEdit', '/superadmin/schools/:itemId/edit'],
]

// Relative to the school base. Static paths come before the ones with an id, so
// 'notices/new' is not read as a notice id.
const SCHOOL_SECTION_PATHS: [Section, string][] = [
	['HomeModal', ''],
	['NoticeList', 'notices'],
	['CreateNotice', 'notices/new'],
	['SeeNotice', 'notices/:itemId'],
	['CreateNewsLetter', 'newsletters/new'],
	['GroupList', 'groups'],
	['SeeGroup', 'groups/:itemId'],
	['HomeReport', 'reports'],
	['GetIndividualReport', 'reports/individual'],
	['GetGrupalReport', 'reports/group'],
	['Profile', 'profile'],
	['EmergencyTypes', 'critical-situations'],
	['StudentsAndUsers', 'users-and-students'],
	['AllGroupList', 'all-groups'],
	['CreateGroup', 'all-groups/new'],
	['CreateUser', 'users/new'],
	['EditUser', 'users/edit'],
	['CreateStudent', 'students/new'],
	['EditStudent', 'students/edit'],
]

function DecodeParam(value: string | undefined): string | undefined {
	if (value === undefined) {
		return undefined
	}
	try {
		return decodeURIComponent(value)
	} catch {
		return value
	}
}

function FillPath(pattern: string, params: Record<string, string | undefined>): string | undefined {
	let isMissingParam = false
	const path = pattern.replace(/:(\w+)/g, (_, paramName: string) => {
		const value = params[paramName]
		if (!value) {
			isMissingParam = true
			return ''
		}
		return encodeURIComponent(value)
	})
	return isMissingParam ? undefined : path
}

export function GetHomeSection(area: AppArea): Section {
	return area === 'superAdmin' ? 'SchoolList' : 'HomeModal'
}

export function GetHomePath(area: AppArea, schoolSK?: string): string {
	if (area === 'superAdmin') {
		return SUPER_ADMIN_HOME_PATH
	}
	return FillPath(SCHOOL_BASE_PATTERNS[area], { schoolSK }) ?? '/'
}

export function GetSuperAdminSchoolPath(schoolSK: string): string {
	return FillPath(SCHOOL_BASE_PATTERNS.superAdmin, { schoolSK }) ?? SUPER_ADMIN_HOME_PATH
}

export function ParseAppPath(pathname: string): AppLocation | undefined {
	for (const [section, path] of SUPER_ADMIN_SECTION_PATHS) {
		const match = matchPath(path, pathname)
		if (match) {
			return { area: 'superAdmin', section, itemId: DecodeParam(match.params.itemId) }
		}
	}
	for (const area of Object.keys(SCHOOL_BASE_PATTERNS) as AppArea[]) {
		const baseMatch = matchPath({ path: SCHOOL_BASE_PATTERNS[area], end: false }, pathname)
		if (!baseMatch) {
			continue
		}
		const schoolSK = DecodeParam(baseMatch.params.schoolSK)
		const pathInsideSchool = pathname.slice(baseMatch.pathnameBase.length) || '/'
		for (const [section, path] of SCHOOL_SECTION_PATHS) {
			const match = matchPath(`/${path}`, pathInsideSchool)
			if (match) {
				return { area, schoolSK, section, itemId: DecodeParam(match.params.itemId) }
			}
		}
		return { area, schoolSK }
	}
	if (matchPath({ path: SUPER_ADMIN_HOME_PATH, end: false }, pathname)) {
		return { area: 'superAdmin' }
	}
	return undefined
}

// Returns undefined when the section needs an id or a school that was not given.
export function GetSectionPath(area: AppArea, schoolSK: string | undefined, section: Section,
	itemId?: string): string | undefined {
	if (area === 'superAdmin') {
		const superAdminPath = SUPER_ADMIN_SECTION_PATHS.find(([pathSection]) => pathSection === section)?.[1]
		if (superAdminPath) {
			return FillPath(superAdminPath, { itemId })
		}
	}
	const sectionPath = SCHOOL_SECTION_PATHS.find(([pathSection]) => pathSection === section)?.[1]
	const schoolBasePath = FillPath(SCHOOL_BASE_PATTERNS[area], { schoolSK })
	if (sectionPath === undefined || !schoolBasePath) {
		return undefined
	}
	if (sectionPath === '') {
		return schoolBasePath
	}
	const filledSectionPath = FillPath(sectionPath, { itemId })
	return filledSectionPath && `${schoolBasePath}/${filledSectionPath}`
}

// React Router keeps the position of every entry it creates in history.state.idx, so a
// position above zero means the previous entry belongs to this app.
export function HasPreviousAppEntry(): boolean {
	const historyState = window.history.state as { idx?: number } | null
	return (historyState?.idx ?? 0) > 0
}
